// hooks/useConversation.js
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import ConversationService from '../services/ConversationService'

// Helper-Funktion zur Deduplizierung von Messages
function deduplicateMessages (messages) {
  if (!messages || messages.length === 0) return []

  const seen = new Map()
  const deduplicated = []

  messages.forEach(msg => {
    // Erstelle einen eindeutigen Key basierend auf mehreren Attributen
    // Verwende timestamp (als String für Vergleichbarkeit), type und content-Anfang
    const timestamp =
      msg.timestamp instanceof Date
        ? msg.timestamp.toISOString()
        : msg.timestamp?.toDate?.()?.toISOString() || msg.timestamp || ''

    const contentPreview = msg.content?.substring(0, 100) || ''
    const type = msg.type || (msg.role === 'assistant' ? 'bot' : 'user')

    const key = `${type}_${timestamp}_${contentPreview}`

    // Nur hinzufügen wenn noch nicht gesehen
    if (!seen.has(key)) {
      seen.set(key, true)
      deduplicated.push(msg)
    }
  })

  // Sortiere nach Timestamp für korrekte Reihenfolge
  return deduplicated.sort((a, b) => {
    const timeA =
      a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp)
    const timeB =
      b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp)
    return timeA - timeB
  })
}

// Helper-Funktion zur Deduplizierung von Conversations
function deduplicateConversations (conversations) {
  if (!conversations || conversations.length === 0) return []

  const seen = new Map()
  const deduplicated = []

  conversations.forEach(conv => {
    if (!seen.has(conv.id)) {
      seen.set(conv.id, true)
      // Dedupliziere auch die Messages innerhalb der Conversation
      if (conv.messages) {
        conv.messages = deduplicateMessages(conv.messages)
      }
      deduplicated.push(conv)
    }
  })

  return deduplicated
}

export function useConversation () {
  const { user } = useAuth()
  const [conversations, setConversations] = useState([])
  const [currentConversation, setCurrentConversation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Alle Conversations laden mit Deduplizierung
  const loadConversations = useCallback(async () => {
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      const userConversations = await ConversationService.getUserConversations(
        user.uid
      )

      // Dedupliziere Conversations bevor sie gesetzt werden
      const deduplicatedConversations =
        deduplicateConversations(userConversations)
      setConversations(deduplicatedConversations)
    } catch (err) {
      setError('Fehler beim Laden der Conversations: ' + err.message)
      console.error('Load conversations error:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Spezifische Conversation laden mit Deduplizierung
  const loadConversation = useCallback(
    async conversationId => {
      if (!user || !conversationId) return null

      setLoading(true)
      setError(null)

      try {
        const conversation = await ConversationService.loadConversation(
          conversationId,
          user.uid
        )

        // Dedupliziere Messages vor dem Setzen
        if (conversation && conversation.messages) {
          conversation.messages = deduplicateMessages(conversation.messages)
        }

        setCurrentConversation(conversation)
        return conversation
      } catch (err) {
        setError('Fehler beim Laden der Conversation: ' + err.message)
        console.error('Load conversation error:', err)
        return null
      } finally {
        setLoading(false)
      }
    },
    [user]
  )

  // Neue Conversation erstellen
  const createConversation = useCallback(
    async (deviceName, initialMessage = null) => {
      if (!user) throw new Error('User nicht authentifiziert')

      setLoading(true)
      setError(null)

      try {
        const conversationId = await ConversationService.createConversation(
          user.uid,
          deviceName,
          initialMessage
        )

        // Conversations-Liste aktualisieren
        await loadConversations()

        return conversationId
      } catch (err) {
        setError('Fehler beim Erstellen der Conversation: ' + err.message)
        console.error('Create conversation error:', err)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [user, loadConversations]
  )

  const addMessage = useCallback(
    async (conversationId, message, sources = [], performance = null) => {
      // Diese Funktion wird nicht mehr vom Chat-Flow verwendet
      // Kann aber für manuelle Message-Hinzufügungen bleiben
      console.warn(
        '⚠️ addMessage aufgerufen - sollte nicht mehr vom Chat verwendet werden'
      )

      if (!user) throw new Error('User nicht authentifiziert')

      try {
        const messageId = await ConversationService.addMessage(
          conversationId,
          message,
          user.uid, // userId parameter for company-scoped path
          sources,
          performance
        )

        // Conversation neu laden um Duplikate zu vermeiden
        if (currentConversation && currentConversation.id === conversationId) {
          await loadConversation(conversationId)
        }

        return messageId
      } catch (err) {
        setError('Fehler beim Hinzufügen der Message: ' + err.message)
        console.error('Add message error:', err)
        throw err
      }
    },
    [user, currentConversation, loadConversation]
  )

  // Conversation archivieren
  const archiveConversation = useCallback(
    async conversationId => {
      if (!user) return

      try {
        await ConversationService.archiveConversation(conversationId, user.uid)

        // Aus lokaler Liste entfernen
        setConversations(prev =>
          prev.filter(conv => conv.id !== conversationId)
        )

        // Falls aktuell geladen, zurücksetzen
        if (currentConversation && currentConversation.id === conversationId) {
          setCurrentConversation(null)
        }
      } catch (err) {
        setError('Fehler beim Archivieren: ' + err.message)
        console.error('Archive conversation error:', err)
      }
    },
    [user, currentConversation]
  )

  // Conversation löschen
  const deleteConversation = useCallback(
    async conversationId => {
      if (!user) return

      try {
        await ConversationService.deleteConversation(conversationId, user.uid)

        // Aus lokaler Liste entfernen
        setConversations(prev =>
          prev.filter(conv => conv.id !== conversationId)
        )

        // Falls aktuell geladen, zurücksetzen
        if (currentConversation && currentConversation.id === conversationId) {
          setCurrentConversation(null)
        }
      } catch (err) {
        setError('Fehler beim Löschen: ' + err.message)
        console.error('Delete conversation error:', err)
      }
    },
    [user, currentConversation]
  )

  // Conversation-Titel updaten
  const updateConversationTitle = useCallback(
    async (conversationId, newTitle) => {
      if (!user) return

      try {
        await ConversationService.updateConversationTitle(
          conversationId,
          user.uid,
          newTitle
        )

        // Lokale Liste aktualisieren
        setConversations(prev =>
          prev.map(conv =>
            conv.id === conversationId ? { ...conv, title: newTitle } : conv
          )
        )

        // Aktuelle Conversation aktualisieren
        if (currentConversation && currentConversation.id === conversationId) {
          setCurrentConversation(prev => ({ ...prev, title: newTitle }))
        }
      } catch (err) {
        setError('Fehler beim Updaten des Titels: ' + err.message)
        console.error('Update title error:', err)
      }
    },
    [user, currentConversation]
  )

  // Conversation wechseln/zurücksetzen mit Deduplizierung
  const setActiveConversation = useCallback(conversation => {
    if (conversation && conversation.messages) {
      conversation.messages = deduplicateMessages(conversation.messages)
    }
    setCurrentConversation(conversation)
  }, [])

  const clearCurrentConversation = useCallback(() => {
    setCurrentConversation(null)
  }, [])

  // Initial load beim User-Wechsel
  useEffect(() => {
    if (user) {
      loadConversations()
    } else {
      setConversations([])
      setCurrentConversation(null)
    }
  }, [user, loadConversations])

  // Zusätzlicher Effect zur periodischen Deduplizierung (optional)
  useEffect(() => {
    // Dedupliziere currentConversation wenn sich Messages ändern
    if (currentConversation && currentConversation.messages) {
      const deduplicatedMessages = deduplicateMessages(
        currentConversation.messages
      )
      if (deduplicatedMessages.length !== currentConversation.messages.length) {
        console.log('Duplikate in currentConversation gefunden und entfernt')
        setCurrentConversation(prev => ({
          ...prev,
          messages: deduplicatedMessages
        }))
      }
    }
  }, [currentConversation?.messages?.length])

  return {
    // State
    conversations,
    currentConversation,
    loading,
    error,

    // Actions
    loadConversations,
    loadConversation,
    createConversation,
    addMessage,
    archiveConversation,
    deleteConversation,
    updateConversationTitle,
    setActiveConversation,
    clearCurrentConversation,

    // Utils
    hasConversations: conversations.length > 0,
    conversationCount: conversations.length,
    clearError: () => setError(null),

    // Neue Utils für Deduplizierung
    deduplicateCurrentMessages: () => {
      if (currentConversation) {
        setCurrentConversation(prev => ({
          ...prev,
          messages: deduplicateMessages(prev.messages || [])
        }))
      }
    }
  }
}
